import { useCallback, useRef, useState } from 'react';

const FADE_DURATION = 300;

export const useToolStatus = () => {
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [toolStatusVisible, setToolStatusVisible] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasToolStatusRef = useRef(false);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const handleToolStatus = useCallback(
    (status: string | null) => {
      clearFadeTimer();
      if (status === null) {
        hasToolStatusRef.current = false;
        setToolStatusVisible(false);
        fadeTimerRef.current = setTimeout(() => {
          setToolStatus(null);
        }, FADE_DURATION);
        return;
      }

      if (hasToolStatusRef.current) {
        setToolStatusVisible(false);
        fadeTimerRef.current = setTimeout(() => {
          setToolStatus(status);
          requestAnimationFrame(() => {
            setToolStatusVisible(true);
          });
        }, FADE_DURATION);
      } else {
        hasToolStatusRef.current = true;
        setToolStatus(status);
        requestAnimationFrame(() => {
          setToolStatusVisible(true);
        });
      }
    },
    [clearFadeTimer],
  );

  const resetToolStatus = useCallback(() => {
    clearFadeTimer();
    setToolStatus(null);
    setToolStatusVisible(false);
    hasToolStatusRef.current = false;
  }, [clearFadeTimer]);

  return { toolStatus, toolStatusVisible, handleToolStatus, clearFadeTimer, resetToolStatus };
};
